import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  FormArray,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Params } from '@angular/router';

import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';

import { ImageResponse } from '@app/api/marketplace/models';
import { environment } from '@environments/environment';
import { ImageParentType } from '@image-upload/enums/image-parent-type.enum';
import {
  ImageUploadActions,
  ImageUploadSelectors,
} from '@image-upload/reducers/image-upload.slice';
import { DialogService } from '@shared/services/dialog.service';

@Component({
  selector: 'app-images-tab-for-products',
  templateUrl: './images-tab-for-products.component.html',
  styleUrls: ['./images-tab-for-products.component.scss'],
})
export class ImagesTabForProductsComponent implements OnInit, OnDestroy {
  @Input() type!: ImageParentType;
  @Input() specParent?: string;
  @Input() maxImagesCount = 3;
  readOnly = false;

  parentId!: string;
  readonly imageBaseUrl = `${environment.imageStorageUrl}/`;

  subs = new Subscription();

  originalNames: Array<string> = [];

  images$: Observable<Array<ImageResponse>> = this.store.select(
    ImageUploadSelectors.selectImages
  );

  form = new UntypedFormGroup({
    imageNames: new FormArray([]),
  });

  get imageNameFormArray() {
    return this.form.controls['imageNames'] as FormArray;
  }

  constructor(
    private readonly store: Store,
    readonly route: ActivatedRoute,
    private readonly dialogService: DialogService
  ) {}

  ngOnInit(): void {
    this.subs.add(
      this.route.queryParams.subscribe((params: Params) => {
        if (params['id']) {
          this.parentId = this.specParent ?? params['id'];
          this.loadImages();
        }
      })
    );

    this.subs.add(
      this.route.parent!.data.subscribe((data) => {
        this.readOnly = data['readOnly'];
      })
    );

    this.subs.add(
      this.images$.subscribe((images) => {
        this.imageNameFormArray.clear();
        this.originalNames = [];
        images.forEach((image) => {
          this.imageNameFormArray.controls.push(
            new UntypedFormGroup({
              name: new UntypedFormControl(image.alt, Validators.required),
            })
          );
          this.originalNames.push(image.alt ?? '');
          if (this.readOnly) {
            this.imageNameFormArray.controls.forEach((control) => {
              control.disable();
            });
          }
        });
      })
    );
  }

  getImageNameForm(index: number): UntypedFormGroup {
    return this.imageNameFormArray.controls[index] as UntypedFormGroup;
  }

  nameChanged(index: number): boolean {
    return this.originalNames[index] !== this.getNameFormValue(index);
  }

  getNameFormValue(index: number) {
    return (this.imageNameFormArray.controls[index] as UntypedFormGroup).get(
      'name'
    )?.value;
  }

  onNameInput(event: KeyboardEvent, index: number, image: ImageResponse) {
    if (event.code === 'Enter') {
      if (this.nameChanged(index)) {
        this.saveNameInput(index, image);
      }
    }
  }

  saveNameInput(index: number, image: ImageResponse) {
    this.store.dispatch(
      ImageUploadActions.updateImageData.trigger({
        imageId: image.id ?? '',
        request: {
          alt: this.getNameFormValue(index),
          isDefault: image.isDefault ?? false,
        },
      })
    );
  }

  loadImages() {
    this.store.dispatch(
      ImageUploadActions.loadImages.trigger({
        request: {
          id: this.parentId,
          type: this.type,
        },
      })
    );
  }

  deleteImage(imageId?: string) {
    if (imageId) {
      this.store.dispatch(ImageUploadActions.deleteImage.trigger({ imageId }));
    }
  }

  setDefault(image: ImageResponse) {
    this.store.dispatch(
      ImageUploadActions.updateImageData.trigger({
        imageId: image.id ?? '',
        request: { alt: image.alt ?? '', isDefault: true },
      })
    );
  }

  public canDeactivate() {
    const dirty = this.imageNameFormArray.controls.some(
      (control) => control.dirty
    );
    return dirty ? this.dialogService.confirmUnsavedChanges() : true;
  }

  ngOnDestroy(): void {
    this.store.dispatch(ImageUploadActions.clearImageStore.trigger());
    this.subs.unsubscribe();
  }
}
